import type { ICSSubscription } from './types'
import { REFRESH_INTERVALS } from './types'

/** Late-bound 참조: core.ts에서 refreshSubscription을 바인딩한다 (순환 의존성 방지) */
type RefreshFn = (
  id: string,
  callback?: (success: boolean) => void,
  options?: { force?: boolean },
) => Promise<void>
let _refreshFn: RefreshFn | null = null

export const _bindRefreshFn = (fn: RefreshFn): void => {
  _refreshFn = fn
}

/** 구독별 auto-refresh 타이머 */
export const refreshTimers = new Map<string, ReturnType<typeof setInterval>>()

/** 구독별 refresh 진행 중 락 (동시 실행 방지) */
export const refreshLocks = new Map<string, Promise<void>>()

/** reloadSubscriptionInstances 직렬화 락 */
export let _reloadLock: Promise<void> = Promise.resolve()

export const _setReloadLock = (lock: Promise<void>): void => {
  _reloadLock = lock
}

/**
 * 단일 구독의 auto-refresh 타이머를 설정한다.
 */
export const _setupTimer = (subscription: ICSSubscription): void => {
  _clearTimer(subscription.id)

  if (!subscription.isSync) return

  const interval = REFRESH_INTERVALS[subscription.refreshInterval]
  if (interval <= 0) return

  // lastSynced 기준 남은 시간 계산
  const lastSynced = subscription.lastSynced ? new Date(subscription.lastSynced).getTime() : 0
  const elapsed = lastSynced > 0 ? Date.now() - lastSynced : interval
  const remaining = Math.max(0, interval - elapsed)

  // 첫 fire는 remaining 후, 이후부터 interval 간격 반복
  const timeout = setTimeout(() => {
    _refreshFn?.(subscription.id)
    const timer = setInterval(() => {
      _refreshFn?.(subscription.id)
    }, interval)
    refreshTimers.set(subscription.id, timer)
  }, remaining)
  refreshTimers.set(subscription.id, timeout)
}

/**
 * 단일 구독의 auto-refresh 타이머를 제거한다.
 */
export const _clearTimer = (subscriptionId: string): void => {
  const existing = refreshTimers.get(subscriptionId)
  if (existing) {
    clearInterval(existing)
    refreshTimers.delete(subscriptionId)
  }
}

/**
 * 모든 타이머와 락을 정리한다.
 */
export const clearAllTimers = (): void => {
  refreshTimers.forEach((timer) => clearInterval(timer))
  refreshTimers.clear()
  refreshLocks.clear()
  _reloadLock = Promise.resolve()
}
